/**
 * Single source of truth for Court Order section types.
 * Mirrors `_VALID_SECTION_TYPES` on the backend with the same
 * canonical strings.
 *
 * The "Add section" picker groups by category so the menu stays
 * scannable; the same labels render on the detail panel and in
 * Court Bundle exports.
 */

/**
 * Iter 127 — Order SUBJECT-MATTER taxonomy. Mirrors
 * `COURT_ORDER_SUBJECTS` on the backend. Distinct from section
 * TYPES above: subject describes the ORDER itself (Maintenance,
 * Access, Safety, Barring, …); sections are the clauses inside
 * an order. Two orders on the same relationship coexist by
 * default as long as their subjects differ.
 */
export const ORDER_SUBJECTS = [
  { value: "maintenance", label: "Maintenance" },
  { value: "access", label: "Access / Parenting time" },
  { value: "custody", label: "Custody" },
  { value: "guardianship", label: "Guardianship" },
  { value: "safety_order", label: "Safety Order", expiring: true },
  { value: "protection_order", label: "Protection Order", expiring: true },
  { value: "barring_order", label: "Barring Order", expiring: true },
  { value: "interim_barring_order", label: "Interim Barring Order", expiring: true },
  { value: "restraining_order", label: "Restraining Order", expiring: true },
  { value: "supervised_contact", label: "Supervised Contact" },
  { value: "passport_travel", label: "Passport / Travel" },
  { value: "medical_decisions", label: "Medical Decisions" },
  { value: "education", label: "Education" },
  { value: "property_financial", label: "Property / Financial" },
  { value: "communication_restrictions", label: "Communication Restrictions" },
  { value: "other", label: "Other (specify)" },
];

export function orderSubjectLabel(subject, subjectOther) {
  if (!subject) return "Unclassified";
  if (subject === "other" && subjectOther) return subjectOther;
  const found = ORDER_SUBJECTS.find((s) => s.value === subject);
  return found ? found.label : subject;
}


export const SECTION_CATEGORIES = [
  {
    key: "parenting",
    label: "Parenting",
    types: [
      { value: "parenting_schedule", label: "Parenting schedule" },
      { value: "school_education", label: "School & education" },
    ],
  },
  {
    key: "contact",
    label: "Contact",
    types: [
      { value: "contact_visits", label: "Contact visits" },
      { value: "supervised_contact", label: "Supervised contact" },
      { value: "handover_arrangements", label: "Handover arrangements" },
      { value: "holiday_arrangements", label: "Holiday arrangements" },
    ],
  },
  {
    key: "financial",
    label: "Financial",
    types: [
      { value: "financial_reimbursement", label: "Financial reimbursement" },
      { value: "child_maintenance", label: "Child maintenance" },
    ],
  },
  {
    key: "welfare",
    label: "Welfare",
    types: [
      { value: "medical_decisions", label: "Medical decisions" },
      { value: "safety_protection", label: "Safety & protection" },
      { value: "third_party_facilitators", label: "Third-party facilitators" },
      { value: "third_party_contact", label: "Third-party contact" },
      { value: "passport_travel", label: "Passport & travel" },
      { value: "communication_rules", label: "Communication rules" },
      { value: "religious_cultural_arrangements", label: "Religious / cultural arrangements" },
      { value: "substance_alcohol_conditions", label: "Substance & alcohol conditions" },
    ],
  },
  {
    key: "restrictions",
    label: "Restrictions",
    types: [
      { value: "specific_issue_orders", label: "Specific issue orders" },
      { value: "prohibited_steps_orders", label: "Prohibited steps orders" },
    ],
  },
  {
    key: "other",
    label: "Other",
    types: [
      { value: "other_directions", label: "Other directions" },
    ],
  },
];

// Court order issuance / variation types — used by the
// "Supersede with new version" flow so the legal lineage
// (Interim → Amended → Consent → Final, etc.) is preserved.
export const ORDER_TYPES = [
  { value: "interim", label: "Interim order" },
  { value: "amended", label: "Amended order" },
  { value: "consent", label: "Consent order" },
  { value: "appeal", label: "Appeal" },
  { value: "final", label: "Final order" },
];

export const ORDER_TYPE_LABEL = Object.fromEntries(ORDER_TYPES.map((t) => [t.value, t.label]));

// Curated "Reason for variation" options. The eighth value
// `other` opens a free-text input so the audit trail can carry
// nuances the dropdown doesn't anticipate.
export const VARIATION_REASONS = [
  { value: "interim_order", label: "Interim order" },
  { value: "consent_variation", label: "Consent variation" },
  { value: "appeal_outcome", label: "Appeal outcome" },
  { value: "review_hearing", label: "Review hearing" },
  { value: "final_determination", label: "Final determination" },
  { value: "emergency_application", label: "Emergency application" },
  { value: "by_agreement", label: "By agreement of the parties" },
  { value: "other", label: "Other (specify)" },
];

export const VARIATION_REASON_LABEL = Object.fromEntries(
  VARIATION_REASONS.map((r) => [r.value, r.label]),
);

// Flat map for fast label lookup. Legacy short-form types resolve
// to the same display label as their newer alias so historical
// orders render cleanly without a data migration.
export const SECTION_LABELS = (() => {
  const out = {};
  for (const cat of SECTION_CATEGORIES) {
    for (const t of cat.types) out[t.value] = t.label;
  }
  // Legacy aliases — keep older orders looking the same.
  out["expense_reimbursement"] = "Financial reimbursement";
  out["communication"] = "Communication rules";
  out["holiday"] = "Holiday arrangements";
  out["other"] = "Other directions";
  out["supervision"] = "Supervised contact";
  return out;
})();

// Types whose detailed editor exists today. Anything outside this
// set falls back to the generic notes-only editor that captures
// the arrangement in the Notes field.
export const RICH_PAYLOAD_TYPES = new Set([
  "financial_reimbursement",
  "expense_reimbursement",
]);

// Canonical equivalence used by the resolver — UI may need this
// to know that an `expense_reimbursement` section IS a financial
// reimbursement section when rendering bundles together.
export function canonicalSectionType(t) {
  switch (t) {
    case "expense_reimbursement":
      return "financial_reimbursement";
    case "communication":
      return "communication_rules";
    case "holiday":
      return "holiday_arrangements";
    case "other":
      return "other_directions";
    case "supervision":
      return "supervised_contact";
    default:
      return t;
  }
}

export const COUNTRY_JURISDICTIONS = [
  { value: "ie", label: "Republic of Ireland" },
  { value: "ni", label: "Northern Ireland" },
  { value: "ew", label: "England & Wales" },
  { value: "sc", label: "Scotland" },
  { value: "other", label: "Other" },
];

export function jurisdictionLabel(code) {
  if (!code) return "—";
  const m = COUNTRY_JURISDICTIONS.find((j) => j.value === code);
  return m ? m.label : code;
}
